import { CartLinesUpdateEvent, ProductSelectEvent } from '@shopify/events';

class ElevoProductBuilder extends HTMLElement {
  connectedCallback() {
    this.product = JSON.parse(this.querySelector('[data-elevo-product-json]').textContent);
    this.bundleProduct = this.product.bundle_product || null;
    const modelImagesEl = this.querySelector('[data-elevo-model-images-json]');
    try {
      this.customModelImages = modelImagesEl ? JSON.parse(modelImagesEl.textContent) : {};
    } catch (e) {
      this.customModelImages = {};
    }
    try {
      this.copy = JSON.parse(this.querySelector('[data-elevo-copy-json]')?.textContent || '{}');
    } catch (e) {
      this.copy = {};
    }
    this.variantIndex = this.buildVariantIndex();
    this.bundleVariantIndex = this.buildVariantIndex(this.bundleProduct);
    this.stockVerificationToken = 0;
    this.heightValues = this.collectValues('height');
    this.sizeValues = this.collectValues('size');
    this.bundleSizeValues = [...new Set(this.bundleVariantIndex.map((variant) => variant.size).filter(Boolean))];
    const defaultSel = this.defaultSelection();
    this.state = {
      mode: this.isFullRangeBundleProduct ? 'product-bundle' : 'single',
      quantity: 1,
      single: defaultSel,
      bundle: [{ ...defaultSel }, { height: '', size: '' }, { height: '', size: '' }],
      bundleHeightOpen: [false, false, false],
    };

    this.render();
    this.syncSelectedVariantMedia();
    this.verifySelectedVariantStock();
    this.syncGalleryModelOrder();

    this.addEventListener('click', this.handleClick);
    this.addEventListener('submit', this.handleSubmit);
    window.addEventListener('popstate', this.handlePopState);
    document.addEventListener('click', this.handleDocumentClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('submit', this.handleSubmit);
    window.removeEventListener('popstate', this.handlePopState);
    document.removeEventListener('click', this.handleDocumentClick);
  }

  handlePopState = () => {
    const sel = this.defaultSelection();
    if (sel.height && (sel.height !== this.state.single.height || sel.size !== this.state.single.size)) {
      this.state.single = { ...sel };
      this.state.bundle[0] = { ...this.state.single };
      this.updateOptionsDOM();
      this.syncSelectedVariantMedia();
    }
  };

  handleDocumentClick = (event) => {
    const link = event.target.closest('a[href*="variant="]');
    if (!link) return;
    try {
      const url = new URL(link.href, window.location.origin);
      if (url.pathname === window.location.pathname) {
        const variantId = url.searchParams.get('variant');
        if (variantId) {
          const match = this.variantIndex.find((v) => String(v.id) === String(variantId));
          if (match) {
            this.state.single = { height: match.height, size: match.size };
            this.state.bundle[0] = { ...this.state.single };
            this.updateOptionsDOM();
            this.syncSelectedVariantMedia();
            const builderEl = document.querySelector('elevo-product-builder');
            if (builderEl) {
              builderEl.scrollIntoView({ behavior: 'smooth' });
            }
          }
        }
      }
    } catch (e) {}
  };

  buildVariantIndex(product = this.product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.map((variant) => ({
      id: variant.id,
      title: variant.title || '',
      available: variant.available === true || Number(variant.inventory_quantity || 0) > 0,
      inventoryQuantity: Number(variant.inventory_quantity || 0),
      isBundleVariant: product === this.bundleProduct,
      sku: variant.sku || '',
      price: Number(variant.price || 0),
      compareAtPrice: Number(variant.compare_at_price || variant.price || 0),
      image: variant.featured_image?.src || this.product.featured_image || '',
      mediaId: variant.featured_image?.id ? String(variant.featured_image.id) : '',
      options: variant.options || [variant.option1, variant.option2, variant.option3].filter(Boolean),
      height: this.extractHeight(variant),
      size: this.extractSize(variant),
    }));
  }

  findBundleVariant(selection) {
    if (!this.bundleVariantIndex.length) return null;
    const matching = this.bundleVariantIndex.filter((variant) => (
      variant.size === selection?.size ||
      variant.sku.endsWith(`-${String(selection?.size || '').replace('-', '')}`)
    ));
    return matching[0] || null;
  }

  get bundleSelection() {
    const size = this.bundleSizeValues.includes(this.state.single.size)
      ? this.state.single.size
      : this.bundleSizeValues[0] || '';
    return { height: '', size };
  }

  extractHeight(variant) {
    const haystack = [
      variant.sku,
      variant.title,
      variant.option1,
      variant.option2,
      variant.option3,
    ]
      .filter(Boolean)
      .join(' / ');
    const match = haystack.match(/\+?\s*\d+(?:[,.]\d+)?\s*cm/i);
    if (match) {
      const value = match[0].replace(/\s+/g, '').replace(',', '.').replace(/cm/i, '');
      return `+${value}cm`;
    }

    const skuHeight = String(variant.sku || '').match(/(?:^|-)HI-(15|25|35)(?:-|$)/i);
    if (skuHeight) return `+${Number(skuHeight[1]) / 10}cm`;

    const decimalHeight = haystack.match(/(?:^|\s|-)(1[.]5|2[.]5|3[.]5)(?:\s|$|\/)/);
    if (decimalHeight) return `+${decimalHeight[1]}cm`;

    return variant.option1 || '';
  }

  extractSize(variant) {
    const values = [
      variant.option1,
      variant.option2,
      variant.option3,
      variant.title,
      variant.sku,
    ].filter(Boolean);
    const value = values.find((item) => /\d{2}\s*[-/]\s*\d{2}/.test(item));
    const match = value?.match(/\d{2}\s*[-/]\s*\d{2}/);
    return match ? match[0].replace(/\s+/g, '') : variant.option2 || '';
  }

  collectValues(type) {
    return [...new Set(this.variantIndex.map((variant) => variant[type]).filter(Boolean))];
  }

  defaultSelection() {
    const urlParams = new URLSearchParams(window.location.search);
    const variantIdParam = urlParams.get('variant');
    let variant = null;
    if (variantIdParam) {
      variant = this.variantIndex.find((item) => String(item.id) === String(variantIdParam));
    }
    if (!variant) {
      variant = this.variantIndex.find((item) => item.available) || this.variantIndex[0];
    }
    return { height: variant?.height || this.heightValues?.[0] || '', size: variant?.size || this.sizeValues?.[0] || '' };
  }

  findVariant(selection) {
    if (!this.variantIndex || this.variantIndex.length === 0) return null;
    if (this.variantIndex.length === 1) {
      return this.variantIndex[0];
    }
    if (!selection) return this.variantIndex[0];

    if (selection.height && selection.size) {
      const exact = this.variantIndex.find(
        (variant) =>
          variant.height === selection.height &&
          variant.size === selection.size &&
          this.isVariantAvailable(variant)
      ) || this.variantIndex.find(
        (variant) => variant.height === selection.height && variant.size === selection.size
      );
      if (exact) return exact;
    }

    if (selection.height) {
      const match = this.variantIndex.find((variant) => variant.height === selection.height && this.isVariantAvailable(variant)) ||
                    this.variantIndex.find((variant) => variant.height === selection.height);
      if (match) return match;
    }

    if (selection.size) {
      const match = this.variantIndex.find((variant) => variant.size === selection.size && this.isVariantAvailable(variant)) ||
                    this.variantIndex.find((variant) => variant.size === selection.size);
      if (match) return match;
    }

    return this.variantIndex.find((v) => this.isVariantAvailable(v)) || this.variantIndex[0] || null;
  }

  isVariantAvailable(variant) {
    if (!variant) return false;
    if (typeof variant.stockAvailable === 'boolean') return variant.stockAvailable;
    return variant.available === true || variant.inventoryQuantity > 0;
  }

  async verifySelectedVariantStock() {
    const token = ++this.stockVerificationToken;
    const isProductBundle = this.state.mode === 'product-bundle';
    const variant = isProductBundle ? this.productBundleVariant : this.singleVariant;
    const productUrl = isProductBundle ? this.bundleProduct?.url : this.product?.url;
    if (!variant?.id || !productUrl) {
      this.updateSubmitButton();
      return;
    }

    this.updateSubmitButton({ label: 'A verificar stock...', disabled: true });
    try {
      // Product JSON may be cached independently for each product. That made it
      // possible for the single product to keep an old sold-out response while
      // the Full Range Bundle already reflected the current inventory.
      const stockUrl = new URL(productUrl, window.location.origin);
      stockUrl.pathname = `${stockUrl.pathname.replace(/\/$/, '').replace(/\.js$/, '')}.js`;
      stockUrl.search = '';
      stockUrl.searchParams.set('_elevo_stock_check', String(Date.now()));

      const response = await fetch(stockUrl.toString(), {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin',
        cache: 'no-store',
      });
      if (!response.ok) throw new Error(`Não foi possível verificar o stock (${response.status}).`);
      const productData = await response.json();
      const liveVariants = Array.isArray(productData.variants) ? productData.variants : [];
      const liveVariant = liveVariants.find((item) => String(item.id) === String(variant.id))
        || liveVariants.find((item) => variant.sku && item.sku === variant.sku);
      if (token !== this.stockVerificationToken) return;
      if (liveVariant) {
        const liveAvailable = liveVariant.available === true;
        const liveHasInventory = Number(liveVariant.inventory_quantity || 0) > 0;
        const liveContinueSelling = liveVariant.inventory_policy === 'continue';
        const localAvailable = variant.available === true || variant.inventoryQuantity > 0;
        variant.stockAvailable = liveAvailable || liveHasInventory || liveContinueSelling || localAvailable;
      }
      this.updateSubmitButton();
    } catch (error) {
      if (token !== this.stockVerificationToken) return;
      console.warn('[ELEVO] Stock verification failed; using local variant data.', error);
      this.updateSubmitButton();
    }
  }

  formatMoney(cents) {
    const currency = window.Shopify?.currency?.active || 'EUR';
    const locale = document.documentElement.lang || 'pt-PT';
    return new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
  }

  escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  get singleVariant() {
    return this.findVariant(this.state.single);
  }

  get productBundleVariant() {
    return this.findBundleVariant(this.bundleSelection);
  }

  get bundleVariants() {
    return this.state.bundle.map((selection) => (selection.height && selection.size ? this.findVariant(selection) : this.variantIndex[0])).filter(Boolean);
  }

  isBundleComplete() {
    return this.state.bundle.every((selection) => Boolean(
      selection.height &&
      selection.size &&
      this.isVariantAvailable(this.findVariant(selection)),
    ));
  }

  getTotals() {
    if (this.state.mode === 'bundle') {
      return this.getBundleTotals();
    }
    const variant = this.singleVariant;
    const total = (variant?.price || 0) * this.state.quantity;
    const compareTotal = (variant?.compareAtPrice || variant?.price || 0) * this.state.quantity;
    return { total, compareTotal, savings: Math.max(compareTotal - total, 0), freeItem: null, freeIndex: -1 };
  }

  getSingleTotals() {
    const variant = this.singleVariant;
    const total = variant?.price || 0;
    const compareTotal = variant?.compareAtPrice || variant?.price || 0;
    return { total, compareTotal, savings: Math.max(compareTotal - total, 0) };
  }

  getBundleTotals() {
    const variants = this.bundleVariants;
    const subtotal = variants.reduce((sum, variant) => sum + (variant?.price || 0), 0);
    const compareSubtotal = variants.reduce((sum, variant) => sum + (variant?.compareAtPrice || variant?.price || 0), 0);
    let freeIndex = 2;
    if (variants.length > 0) {
      let lowestPrice = Infinity;
      variants.forEach((variant, index) => {
        if (variant && variant.price <= lowestPrice) {
          lowestPrice = variant.price;
          freeIndex = index;
        }
      });
    }
    const freeItem = freeIndex >= 0 && freeIndex < variants.length ? variants[freeIndex] : null;
    const total = Math.max(0, subtotal - (freeItem?.price || 0));
    return {
      total,
      compareTotal: compareSubtotal,
      savings: Math.max(compareSubtotal - total, 0),
      freeItem,
      freeIndex,
    };
  }

  isAvailable(selection) {
    if (!selection?.height || !selection?.size) return false;
    return this.isVariantAvailable(this.findVariant(selection));
  }

  getHeightLabel(height) {
    const labels = {
      '+1.5cm': 'LOW',
      '+2.5cm': 'VIBE',
      '+3.5cm': 'FLEX',
    };
    return labels[height] || height;
  }

  getHeightCaption(height) {
    const labels = {
      '+1.5cm': 'discreto',
      '+2.5cm': 'equilibrado',
      '+3.5cm': 'máximo',
    };
    return labels[height] || 'modelo';
  }

  getHeightImage(height) {
    if (!height) return '';
    if (this.customModelImages && this.customModelImages[height]) {
      return this.customModelImages[height];
    }
    const variant = this.variantIndex.find((v) => v.height === height && v.image);
    return variant?.image || '';
  }

  getBundleDiscountPercent() {
    const totals = this.getBundleTotals();
    if (!totals.compareTotal || totals.compareTotal <= totals.total) return 0;
    return Math.round(((totals.compareTotal - totals.total) / totals.compareTotal) * 100);
  }

  getSizeCaption(size) {
    return 'EU';
  }

  get hasHeightOptions() {
    if (this.isSingleVariantProduct) return false;
    if (!this.heightValues || this.heightValues.length === 0) return false;
    const valid = this.heightValues.filter((val) => val && !val.toLowerCase().includes('default'));
    return valid.length > 1;
  }

  get hasSizeOptions() {
    if (this.isSingleVariantProduct) return false;
    if (!this.sizeValues || this.sizeValues.length === 0) return false;
    const valid = this.sizeValues.filter((val) => val && !val.toLowerCase().includes('default'));
    return valid.length > 0;
  }

  get isSingleVariantProduct() {
    if (!this.product || !this.product.variants) return true;
    if (this.product.variants.length <= 1) return true;
    const validHeights = (this.heightValues || []).filter((val) => val && !val.toLowerCase().includes('default'));
    const validSizes = (this.sizeValues || []).filter((val) => val && !val.toLowerCase().includes('default'));
    return validHeights.length <= 1 && validSizes.length === 0;
  }

  // Keep the former promotion implementation available for a future campaign.
  get isPromoBundleEnabled() {
    return false;
  }

  get isFullRangeBundleProduct() {
    return String(this.product?.title || '').toLowerCase().includes('full range bundle');
  }

  get fullRangeShippingSavings() {
    return 599;
  }

  renderTrustHeader() {
    const shippingText = this.copy.shipping_text === 'em compras superiores a 40€'
      ? 'em compras superiores a 30€'
      : (this.copy.shipping_text || 'em compras superiores a 30€');
    const copy = {
      ...this.copy,
      rating: '4.8/5',
      customers: 'Clientes ELEVO',
      shipping_label: 'Envio Grátis',
      shipping_text: shippingText,
    };
    return `
      <div class="elevo-builder__trust">
        <div class="elevo-builder__stars" aria-label="Avaliação 4.8 de 5">
          <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
        </div>
        <strong>${this.escapeHtml(copy.rating)}</strong>
        <span>${this.escapeHtml(copy.customers)}</span>
      </div>
      <div class="elevo-builder__shipping-highlight">
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <rect x="1" y="3" width="15" height="13"></rect>
          <polygon points="16 8 20 8 23 11 23 16 16 16 16 8"></polygon>
          <circle cx="5.5" cy="18.5" r="2.5"></circle>
          <circle cx="18.5" cy="18.5" r="2.5"></circle>
        </svg>
        <span><strong>${this.escapeHtml(copy.shipping_label)}</strong> ${this.escapeHtml(copy.shipping_text)}</span>
      </div>
    `;
  }

  renderFullRangeDescription() {
    if (!this.isFullRangeBundleProduct) return '';
    const copy = {
      bundle_description_title: 'O que inclui o Full Range Bundle?',
      bundle_vibe_title: 'Vibe · +2.5cm',
      bundle_vibe_description: 'Aumento discreto para conforto diário e uma adaptação natural ao calçado.',
      bundle_flex_title: 'Flex · +3.5cm',
      bundle_flex_description: 'Mais altura e suporte, mantendo uma sensação confortável e estável.',
      bundle_description_footer: 'Escolhe o teu tamanho e recebe os dois modelos no mesmo bundle.',
      ...this.copy,
    };
    return `
      <section class="elevo-builder__bundle-description-panel" aria-labelledby="elevo-full-range-description-title">
        <h2 id="elevo-full-range-description-title">${this.escapeHtml(copy.bundle_description_title)}</h2>
        <div class="elevo-builder__bundle-models">
          <div class="elevo-builder__bundle-model">
            <strong>${this.escapeHtml(copy.bundle_vibe_title)}</strong>
            <span>${this.escapeHtml(copy.bundle_vibe_description)}</span>
          </div>
          <div class="elevo-builder__bundle-model">
            <strong>${this.escapeHtml(copy.bundle_flex_title)}</strong>
            <span>${this.escapeHtml(copy.bundle_flex_description)}</span>
          </div>
        </div>
        <p>${this.escapeHtml(copy.bundle_description_footer)}</p>
      </section>
    `;
  }

  renderHeightPreview() {
    if (this.isSingleVariantProduct || !this.hasHeightOptions) {
      return '';
    }
    const validHeights = this.heightValues.filter((val) => val && !val.toLowerCase().includes('default'));
    if (validHeights.length <= 1) {
      return '';
    }
    return `
      <div class="elevo-builder__explore">
        <span>${this.escapeHtml(this.copy.explore_label || 'Explora os modelos')}</span>
        <div class="elevo-builder__options elevo-builder__options--height">
          ${validHeights
            .map((height) => {
              const selected = this.state.single.height === height;
              const image = this.getHeightImage(height);
              const isHot = height.includes('3.5');
              return `
                <button
                  type="button"
                  class="elevo-builder__option${selected ? ' is-active' : ''}"
                  data-elevo-option="height"
                  data-elevo-value="${this.escapeHtml(height)}"
                  data-elevo-scope="single"
                  aria-label="${this.escapeHtml(this.getHeightLabel(height))} ${this.escapeHtml(height)}"
                >
                  ${isHot ? `<span class="elevo-builder__hot-badge">🔥 HOT</span>` : ''}
                  ${image ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(this.getHeightLabel(height))}" class="elevo-builder__option-img">` : ''}
                  <span>${this.escapeHtml(this.getHeightLabel(height))} <small class="elevo-builder__height-badge">${this.escapeHtml(height)}</small></span>
                </button>
              `;
            })
            .join('')}
        </div>
      </div>
    `;
  }

  renderOptionGroup(selection, scope, index) {
    const suffix = index === undefined ? '' : `-${index}`;
    const renderButtons = (type, values) => `
      <div class="elevo-builder__option-group">
        <div class="elevo-builder__label">${type === 'height' ? 'Altura' : 'Tamanho'}</div>
        <div class="elevo-builder__options elevo-builder__options--${type}">
          ${values
            .map((value) => {
              const next = { ...selection, [type]: value };
              const active = selection[type] === value;
              const disabled = selection.height && selection.size ? !this.findVariant(next) : false;
              const escapedValue = this.escapeHtml(value);
              const isHot = type === 'height' && value.includes('3.5');
              return `
                <button
                  type="button"
                  class="elevo-builder__option${active ? ' is-active' : ''}"
                  data-elevo-option="${type}"
                  data-elevo-value="${escapedValue}"
                  data-elevo-scope="${scope}"
                  ${index === undefined ? '' : `data-elevo-index="${index}"`}
                  ${disabled ? 'disabled' : ''}
                >
                  ${isHot ? `<span class="elevo-builder__hot-badge">🔥 HOT</span>` : ''}
                  ${type === 'height'
                    ? `<span>${this.escapeHtml(this.getHeightLabel(value))} <small class="elevo-builder__height-badge">${escapedValue}</small></span>`
                    : `<span>${escapedValue}</span><small>${this.escapeHtml(this.getSizeCaption(value))}</small>`}
                </button>
              `;
            })
            .join('')}
        </div>
      </div>
    `;

    return `
      <div class="elevo-builder__selectors" data-selector-id="${scope}${suffix}">
        ${renderButtons('height', this.heightValues)}
        ${renderButtons('size', this.sizeValues)}
      </div>
    `;
  }

  renderOptionButtons(selection, scope, index, type) {
    const values = type === 'height' ? this.heightValues : this.sizeValues;
    return `
      <div class="elevo-builder__options elevo-builder__options--${type}">
        ${values
          .map((value) => {
            const next = { ...selection, [type]: value };
            const active = selection[type] === value;
            const disabled = selection.height && selection.size ? !this.findVariant(next) : false;
            const escapedValue = this.escapeHtml(value);
            const isHot = type === 'height' && value.includes('3.5');
            return `
              <button
                type="button"
                class="elevo-builder__option${type === 'size' ? ' elevo-builder__option--size' : ''}${active ? ' is-active' : ''}"
                data-elevo-option="${type}"
                data-elevo-value="${escapedValue}"
                data-elevo-scope="${scope}"
                ${index === undefined ? '' : `data-elevo-index="${index}"`}
                ${disabled ? 'disabled' : ''}
                >
                ${isHot ? `<span class="elevo-builder__hot-badge">🔥 HOT</span>` : ''}
                ${type === 'height'
                  ? `<span>${this.escapeHtml(this.getHeightLabel(value))} <small class="elevo-builder__height-badge">${escapedValue}</small></span>`
                  : `<strong class="elevo-builder__size-text">EU ${escapedValue}</strong>`}
              </button>
            `;
          })
          .join('')}
      </div>
    `;
  }

  renderBundleItem(selection, index, freeIndex) {
    const isFree = index === freeIndex && Boolean(selection.height && selection.size);
    const variant = this.findVariant(selection);
    const heightOpen = this.state.bundleHeightOpen[index];
    const image = this.getHeightImage(selection.height);

    let summaryText = 'Por selecionar';
    if (selection.height && selection.size) {
      summaryText = `${selection.height} / ${selection.size}`;
    } else if (selection.height) {
      summaryText = selection.height;
    } else if (selection.size) {
      summaryText = selection.size;
    }

    const modelLabel = selection.height ? this.getHeightLabel(selection.height) : 'Escolher modelo';
    const modelSub = selection.height ? selection.height : '';

    return `
      <div class="elevo-builder__bundle-item${isFree ? ' is-free' : ''}">
        <div class="elevo-builder__bundle-head">
          <span>${index === 0 ? '1.º par' : index === 1 ? '2.º par' : '3.º par'}</span>
          <small class="elevo-builder__bundle-summary">${this.escapeHtml(summaryText)}</small>
          ${variant ? `<em class="elevo-builder__bundle-price-val">${this.formatMoney(variant.price)}</em>` : ''}
          ${isFree ? '<strong>Grátis</strong>' : ''}
        </div>
        <div class="elevo-builder__bundle-model">
          <button type="button" class="elevo-builder__bundle-model-btn" data-elevo-bundle-height-toggle="${index}">
            ${image ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(modelLabel)}" class="elevo-builder__option-img">` : ''}
            <span>${this.escapeHtml(modelLabel)}${modelSub ? ` <small class="elevo-builder__height-badge">${this.escapeHtml(modelSub)}</small>` : ''}</span>
            <svg class="elevo-builder__chevron${heightOpen ? ' is-open' : ''}" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
          </button>
          <div class="elevo-builder__bundle-model-picker${heightOpen ? ' is-open' : ''}">
            ${this.heightValues
              .map((value) => {
                const active = selection.height === value;
                const img = this.getHeightImage(value);
                const isHot = value.includes('3.5');
                return `
                  <button
                    type="button"
                    class="elevo-builder__option${active ? ' is-active' : ''}"
                    data-elevo-option="height"
                    data-elevo-value="${this.escapeHtml(value)}"
                    data-elevo-scope="bundle"
                    data-elevo-index="${index}"
                  >
                    ${isHot ? `<span class="elevo-builder__hot-badge">🔥 HOT</span>` : ''}
                    ${img ? `<img src="${this.escapeHtml(img)}" alt="${this.escapeHtml(this.getHeightLabel(value))}" class="elevo-builder__option-img">` : ''}
                    <span>${this.escapeHtml(this.getHeightLabel(value))} <small class="elevo-builder__height-badge">${this.escapeHtml(value)}</small></span>
                  </button>
                `;
              })
              .join('')}
          </div>
        </div>
        ${this.renderOptionButtons(selection, 'bundle', index, 'size')}
      </div>
    `;
  }

  renderPaymentIcons() {
    const template = this.querySelector('[data-elevo-payment-icons-template]');
    if (template && template.innerHTML.trim()) {
      return template.innerHTML;
    }
    return '';
  }

  render() {
    if (this.isSingleVariantProduct && !this.isFullRangeBundleProduct) {
      this.state.mode = 'single';
    }
    const totals = this.getTotals();
    const bundleTotals = this.getBundleTotals();
    const single = this.singleVariant;
    const bundleComplete = this.isBundleComplete();
    const available = this.state.mode === 'bundle'
      ? bundleComplete
      : this.isVariantAvailable(this.state.mode === 'product-bundle' ? this.productBundleVariant : single);
    const singlePrice = single?.price || 0;
    const singleCompare = single?.compareAtPrice || singlePrice;
    const productBundle = this.productBundleVariant || single;
    const productBundlePrice = productBundle?.price || 0;
    const productBundleCompare = productBundle?.compareAtPrice || productBundlePrice;
    const bundleCompare = productBundleCompare;
    const bundleSavings = Math.max(bundleCompare - productBundlePrice, 0) + this.fullRangeShippingSavings;
    const bundleDiscount = bundleCompare > productBundlePrice
      ? Math.round(((bundleCompare - productBundlePrice) / bundleCompare) * 100)
      : 0;
    const singleDiscount = singleCompare > singlePrice ? Math.round(((singleCompare - singlePrice) / singleCompare) * 100) : 0;
    const bundlePreviewTotal = bundleTotals.total;
    const bundlePreviewCompare = bundleTotals.compareTotal;
    const bundlePreviewSavings = bundleTotals.savings;

    this.querySelector('[data-elevo-builder-ui]').innerHTML = `
      ${this.renderTrustHeader()}
      ${this.renderFullRangeDescription()}
      ${this.isFullRangeBundleProduct ? '' : this.renderHeightPreview()}

      <div class="elevo-builder__cards">
        ${!this.isFullRangeBundleProduct ? `
          <div class="elevo-builder__card-box${this.state.mode === 'single' ? ' is-active' : ''}">
          <button type="button" class="elevo-builder__card-header" data-elevo-mode="single">
            ${this.isSingleVariantProduct ? '' : '<span class="elevo-builder__radio" aria-hidden="true"></span>'}
            <span class="elevo-builder__mode-copy">
              <span>${this.isSingleVariantProduct ? 'Opção de compra' : 'Adicionar apenas 1 par'}</span>
            </span>
            <span class="elevo-builder__mode-price">
              ${singleDiscount > 0 ? `<span class="elevo-builder__discount-pct">-${singleDiscount}%</span>` : ''}
              <strong>${this.formatMoney(singlePrice)}</strong>
              ${singleCompare > singlePrice ? `<s>${this.formatMoney(singleCompare)}</s>` : ''}
            </span>
          </button>
          ${this.hasSizeOptions ? `
            <div class="elevo-builder__card-body">
              <div class="elevo-builder__card-inner">
                ${this.renderSingle()}
              </div>
            </div>
          ` : ''}
          </div>
        ` : ''}

        ${(!this.isSingleVariantProduct || this.isFullRangeBundleProduct) ? `
          <div class="elevo-builder__card-box elevo-builder__card-box--product-bundle${this.state.mode === 'product-bundle' ? ' is-active' : ''}">
            <span class="elevo-builder__most-chosen">O mais escolhido</span>
            <button type="button" class="elevo-builder__card-header" data-elevo-mode="product-bundle">
              <span class="elevo-builder__radio" aria-hidden="true"></span>
              <span class="elevo-builder__mode-copy">
                <span class="elevo-builder__bundle-title">Full Range Bundle</span>
                <ul class="elevo-builder__bundle-description">
                  <li>Vibe · +2.5cm</li>
                  <li>Flex · +3.5cm</li>
                </ul>
                <strong class="elevo-builder__free-shipping">
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M3 6h11v10H3z"></path>
                    <path d="M14 9h4l3 3v4h-7z"></path>
                    <path d="M6.5 19a2 2 0 1 0 0-4 2 2 0 0 0 0 4Zm11 0a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"></path>
                  </svg>
                  <span>Envio grátis</span>
                </strong>
              </span>
              <span class="elevo-builder__mode-price">
                ${bundleDiscount > 0 ? `<span class="elevo-builder__discount-pct">-${bundleDiscount}%</span>` : ''}
                <strong>${this.formatMoney(productBundlePrice)}</strong>
                ${bundleCompare > productBundlePrice ? `<s>${this.formatMoney(bundleCompare)}</s>` : ''}
                <em data-elevo-product-bundle-savings>${bundleSavings > 0 ? `Poupa ${this.formatMoney(bundleSavings)}` : 'Preço especial do bundle'}</em>
              </span>
            </button>
            <div class="elevo-builder__card-body">
              <div class="elevo-builder__card-inner">
                ${this.renderSingle('product-bundle')}
              </div>
            </div>
          </div>
        ` : ''}

        ${this.isPromoBundleEnabled && !this.isSingleVariantProduct ? `
          <div class="elevo-builder__card-box elevo-builder__card-box--bundle${this.state.mode === 'bundle' ? ' is-active' : ''}">
            <span class="elevo-builder__popular">Mais escolhido</span>
            <button type="button" class="elevo-builder__card-header" data-elevo-mode="bundle">
              <span class="elevo-builder__radio" aria-hidden="true"></span>
              <span class="elevo-builder__mode-copy">
                <span>Compra 2, leva 1 grátis</span>
                <em>Poupas ${this.formatMoney(bundlePreviewSavings)}</em>
              </span>
              <span class="elevo-builder__mode-price">
                ${this.getBundleDiscountPercent() > 0 ? `<span class="elevo-builder__discount-pct">-${this.getBundleDiscountPercent()}%</span>` : ''}
                <strong>${this.formatMoney(bundlePreviewTotal)}</strong>
                ${bundlePreviewCompare > bundlePreviewTotal ? `<s>${this.formatMoney(bundlePreviewCompare)}</s>` : ''}
              </span>
            </button>
            <div class="elevo-builder__card-body">
              <div class="elevo-builder__card-inner">
                ${this.renderBundle(totals.freeIndex)}
              </div>
            </div>
          </div>
        ` : ''}
      </div>

      <form class="elevo-builder__form">
        <input type="hidden" name="id" value="${single?.id || ''}">
        <div class="elevo-builder__actions elevo-builder__actions--${this.state.mode}">
          ${this.renderQuantity()}
          <button type="submit" class="elevo-builder__submit" ${available ? '' : 'disabled'}>
            ${this.state.mode === 'bundle'
              ? (bundleComplete ? 'Levar 1 grátis - comprar agora' : 'Escolhe as opções para os 3 pares')
              : (available
                ? (this.state.mode === 'product-bundle' ? 'Adicionar bundle' : 'Adicionar ao carrinho')
                : 'Sem Stock')}
          </button>
        </div>
        <div class="elevo-builder__payments" aria-label="Métodos de pagamento">
          ${this.renderPaymentIcons()}
        </div>
        <p class="elevo-builder__status" aria-live="polite" data-elevo-status></p>
      </form>
    `;
  }

  renderSingle(scope = 'single') {
    if (this.isSingleVariantProduct || !this.hasSizeOptions) {
      return '';
    }
    const values = scope === 'product-bundle' ? this.bundleSizeValues : this.sizeValues;
    const validSizes = values.filter((val) => val && !val.toLowerCase().includes('default'));
    if (validSizes.length === 0) {
      return '';
    }
    const selection = scope === 'product-bundle' ? this.bundleSelection : this.state.single;
    const renderSizeButtons = () => `
      <div class="elevo-builder__option-group">
        <div class="elevo-builder__label">Tamanho</div>
        <div class="elevo-builder__options elevo-builder__options--size">
          ${validSizes
            .map((value) => {
              const next = { ...selection, size: value };
              const active = selection.size === value;
              const disabled = scope === 'product-bundle'
                ? !this.findBundleVariant(next)
                : !this.findVariant(next);
              const escapedValue = this.escapeHtml(value);
              return `
                <button
                  type="button"
                  class="elevo-builder__option elevo-builder__option--size${active ? ' is-active' : ''}"
                  data-elevo-option="size"
                  data-elevo-value="${escapedValue}"
                  data-elevo-scope="${scope}"
                  ${disabled ? 'disabled' : ''}
                >
                  <strong class="elevo-builder__size-text">EU ${escapedValue}</strong>
                </button>
              `;
            })
            .join('')}
        </div>
      </div>
    `;
    return `
      <div class="elevo-builder__single">
        <div class="elevo-builder__selectors">
          ${renderSizeButtons()}
        </div>
      </div>
    `;
  }

  renderBundle(freeIndex) {
    return `
      <div class="elevo-builder__bundle">
        ${this.state.bundle.map((selection, index) => this.renderBundleItem(selection, index, freeIndex)).join('')}
      </div>
    `;
  }

  renderQuantity() {
    return `
      <div class="elevo-builder__quantity" aria-label="Quantidade">
        <button type="button" data-elevo-quantity="-1">-</button>
        <span>${this.state.quantity}</span>
        <button type="button" data-elevo-quantity="1">+</button>
      </div>
    `;
  }

  previewBundleTotal() {
    const totals = this.getTotals();
    return totals.total;
  }

  previewBundleCompare() {
    const totals = this.getTotals();
    return totals.compareTotal;
  }

  updateSubmitButton({ loading = false, label = '', disabled } = {}) {
    const submitButton = this.querySelector('.elevo-builder__submit');
    if (!submitButton) return;

    if (loading) {
      submitButton.disabled = true;
      submitButton.innerHTML = '<span class="elevo-builder__spinner" aria-hidden="true"></span><span>A adicionar...</span>';
      return;
    }

    const bundleComplete = this.isBundleComplete();
    const variant = this.state.mode === 'product-bundle'
      ? this.productBundleVariant
      : this.singleVariant;
    const available = this.state.mode === 'bundle'
      ? bundleComplete
      : this.isVariantAvailable(variant);
    const defaultLabel = this.state.mode === 'bundle'
      ? (bundleComplete ? 'Levar 1 grátis - comprar agora' : 'Escolhe as opções para os 3 pares')
      : (this.state.mode === 'product-bundle' ? 'Adicionar bundle' : 'Adicionar ao carrinho');
    const buttonLabel = label || (available ? defaultLabel : 'Sem Stock');

    submitButton.disabled = disabled ?? (!available && !label);
    submitButton.textContent = buttonLabel;
  }

  updateModeDOM() {
    const singleCard = this.querySelector('.elevo-builder__card-box:not(.elevo-builder__card-box--bundle):not(.elevo-builder__card-box--product-bundle)');
    const bundleCard = this.querySelector('.elevo-builder__card-box--bundle');
    const actionsContainer = this.querySelector('.elevo-builder__actions');
    if (singleCard) singleCard.classList.toggle('is-active', this.state.mode === 'single');
    const productBundleCard = this.querySelector('.elevo-builder__card-box--product-bundle');
    if (productBundleCard) productBundleCard.classList.toggle('is-active', this.state.mode === 'product-bundle');
    if (bundleCard) bundleCard.classList.toggle('is-active', this.state.mode === 'bundle');

    if (actionsContainer) {
      actionsContainer.className = `elevo-builder__actions elevo-builder__actions--${this.state.mode}`;
    }

    this.updateSubmitButton();

    const qtyContainer = this.querySelector('.elevo-builder__quantity');
    if (!qtyContainer && actionsContainer) {
      actionsContainer.insertAdjacentHTML('afterbegin', this.renderQuantity());
    }
  }

  updateOptionsDOM(clickedButton) {
    const scope = clickedButton ? clickedButton.dataset.elevoScope : null;
    const type = clickedButton ? clickedButton.dataset.elevoOption : null;
    const index = clickedButton ? clickedButton.dataset.elevoIndex : null;

    if (clickedButton) {
      const container = clickedButton.closest('.elevo-builder__options, .elevo-builder__bundle-model-picker');
      if (container) {
        container.querySelectorAll('[data-elevo-option]').forEach((btn) => {
          btn.classList.toggle('is-active', btn === clickedButton);
        });
      }
    }

    // Sync all bundle items with state
    const totals = this.getTotals();
    const bundleItems = this.querySelectorAll('.elevo-builder__bundle-item');
    bundleItems.forEach((item, idx) => {
      const sel = this.state.bundle[idx];
      const isFree = idx === totals.freeIndex && Boolean(sel && sel.height && sel.size);
      item.classList.toggle('is-free', isFree);

      if (sel) {
        // Summary text
        const summaryEl = item.querySelector('.elevo-builder__bundle-summary');
        if (summaryEl) {
          let summaryText = 'Por selecionar';
          if (sel.height && sel.size) summaryText = `${sel.height} / ${sel.size}`;
          else if (sel.height) summaryText = sel.height;
          else if (sel.size) summaryText = sel.size;
          summaryEl.textContent = summaryText;
        }

        // Price display
        let priceEl = item.querySelector('.elevo-builder__bundle-price-val');
        const variant = this.findVariant(sel);
        if (variant) {
          if (!priceEl) {
            const summaryEl = item.querySelector('.elevo-builder__bundle-summary');
            if (summaryEl) {
              summaryEl.insertAdjacentHTML('afterend', `<em class="elevo-builder__bundle-price-val">${this.formatMoney(variant.price)}</em>`);
            }
          } else {
            priceEl.textContent = this.formatMoney(variant.price);
          }
        } else if (priceEl) {
          priceEl.remove();
        }

        // Model button label and image
        const labelEl = item.querySelector('.elevo-builder__bundle-model-btn span');
        const modelBtn = item.querySelector('.elevo-builder__bundle-model-btn');
        let imgEl = item.querySelector('.elevo-builder__bundle-model-btn .elevo-builder__option-img');

        if (labelEl) {
          const modelLabel = sel.height ? this.getHeightLabel(sel.height) : 'Escolher modelo';
          const modelSub = sel.height ? sel.height : '';
          labelEl.innerHTML = `${this.escapeHtml(modelLabel)}${modelSub ? ` <small>${this.escapeHtml(modelSub)}</small>` : ''}`;
        }

        if (sel.height) {
          const newImg = this.getHeightImage(sel.height);
          if (newImg) {
            if (!imgEl && modelBtn) {
              modelBtn.insertAdjacentHTML('afterbegin', `<img src="${this.escapeHtml(newImg)}" alt="${this.escapeHtml(this.getHeightLabel(sel.height))}" class="elevo-builder__option-img">`);
            } else if (imgEl) {
              imgEl.src = newImg;
              imgEl.style.display = '';
            }
          }
        } else if (imgEl) {
          imgEl.style.display = 'none';
        }

        // Update active class on size options for this item
        const sizeBtns = item.querySelectorAll('[data-elevo-option="size"]');
        sizeBtns.forEach((btn) => {
          btn.classList.toggle('is-active', btn.dataset.elevoValue === sel.size);
        });

        // Update active class on model picker options for this item
        const heightBtns = item.querySelectorAll('.elevo-builder__bundle-model-picker [data-elevo-option="height"]');
        heightBtns.forEach((btn) => {
          btn.classList.toggle('is-active', btn.dataset.elevoValue === sel.height);
        });
      }

      let gratisBadge = item.querySelector('.elevo-builder__bundle-head strong');
      if (isFree && !gratisBadge) {
        const head = item.querySelector('.elevo-builder__bundle-head');
        if (head) head.insertAdjacentHTML('beforeend', '<strong>Grátis</strong>');
      } else if (!isFree && gratisBadge) {
        gratisBadge.remove();
      }
    });

    // Always update top single height buttons and single size buttons active state
    const topHeightBtns = this.querySelectorAll('.elevo-builder__explore [data-elevo-option="height"]');
    topHeightBtns.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.elevoValue === this.state.single.height);
    });

    const singleSizeBtns = this.querySelectorAll('.elevo-builder__single [data-elevo-option="size"]');
    singleSizeBtns.forEach((btn) => {
      btn.classList.toggle('is-active', btn.dataset.elevoValue === this.state.single.size);
    });

    if (scope === 'bundle' && type === 'height' && index !== undefined) {
      const bundleItem = clickedButton.closest('.elevo-builder__bundle-item');
      if (bundleItem) {
        const picker = bundleItem.querySelector('.elevo-builder__bundle-model-picker');
        const chevron = bundleItem.querySelector('.elevo-builder__chevron');
        if (picker) picker.classList.remove('is-open');
        if (chevron) chevron.classList.remove('is-open');
      }
    }

    // Update prices in DOM
    const singleTotals = this.getSingleTotals();
    const priceLine = this.querySelector('.elevo-builder__price-line');
    if (priceLine) {
      const discount = singleTotals.compareTotal > singleTotals.total
        ? Math.round(((singleTotals.compareTotal - singleTotals.total) / singleTotals.compareTotal) * 100)
        : 0;
      priceLine.innerHTML = `
        ${singleTotals.compareTotal > singleTotals.total ? `<span class="elevo-builder__compare">${this.formatMoney(singleTotals.compareTotal)}</span>` : ''}
        <strong>${this.formatMoney(singleTotals.total)}</strong>
        ${discount > 0 ? `<span class="elevo-builder__discount-pct elevo-builder__discount-pct--large">-${discount}%</span>` : ''}
      `;
    }

    const singleCardPrices = this.querySelectorAll(
      '.elevo-builder__card-box:not(.elevo-builder__card-box--bundle):not(.elevo-builder__card-box--product-bundle) .elevo-builder__mode-price',
    );
    if (singleCardPrices.length > 0) {
      const single = this.singleVariant;
      const productBundle = this.productBundleVariant || single;
      const singlePrice = single?.price || 0;
      const singleCompare = single?.compareAtPrice || singlePrice;
      const singleDiscount = singleCompare > singlePrice ? Math.round(((singleCompare - singlePrice) / singleCompare) * 100) : 0;
      const priceMarkup = `
        ${singleDiscount > 0 ? `<span class="elevo-builder__discount-pct">-${singleDiscount}%</span>` : ''}
        <strong>${this.formatMoney(singlePrice)}</strong>
        ${singleCompare > singlePrice ? `<s>${this.formatMoney(singleCompare)}</s>` : ''}
      `;
      singleCardPrices.forEach((price) => {
        price.innerHTML = priceMarkup;
      });

      const productBundlePrice = this.querySelector('.elevo-builder__card-box--product-bundle .elevo-builder__mode-price');
      if (productBundlePrice) {
        const bundlePrice = productBundle?.price || 0;
        const bundleProductCompare = productBundle?.compareAtPrice || bundlePrice;
        const bundleCompare = bundleProductCompare;
        const bundleSavings = Math.max(bundleCompare - bundlePrice, 0) + this.fullRangeShippingSavings;
        const bundleDiscount = bundleCompare > bundlePrice
          ? Math.round(((bundleCompare - bundlePrice) / bundleCompare) * 100)
          : 0;
        productBundlePrice.innerHTML = `
          ${bundleDiscount > 0 ? `<span class="elevo-builder__discount-pct">-${bundleDiscount}%</span>` : ''}
          <strong>${this.formatMoney(bundlePrice)}</strong>
          ${bundleCompare > bundlePrice ? `<s>${this.formatMoney(bundleCompare)}</s>` : ''}
          <em data-elevo-product-bundle-savings>${bundleSavings > 0 ? `Poupa ${this.formatMoney(bundleSavings)}` : 'Preço especial do bundle'}</em>
        `;
      }

    }

    const bundleCardPrice = this.querySelector('.elevo-builder__card-box--bundle .elevo-builder__mode-price');
    if (bundleCardPrice) {
      const bundleTotals = this.getBundleTotals();
      const bundleTotal = bundleTotals.total;
      const bundleCompare = bundleTotals.compareTotal;
      const bundleDiscountPct = this.getBundleDiscountPercent();
      bundleCardPrice.innerHTML = `
        ${bundleDiscountPct > 0 ? `<span class="elevo-builder__discount-pct">-${bundleDiscountPct}%</span>` : ''}
        <strong>${this.formatMoney(bundleTotal)}</strong>
        ${bundleCompare > bundleTotal ? `<s>${this.formatMoney(bundleCompare)}</s>` : ''}
      `;
    }

    const bundleSavingsEm = this.querySelector('.elevo-builder__card-box--bundle .elevo-builder__mode-copy em');
    if (bundleSavingsEm) {
      const bundleTotals = this.getBundleTotals();
      bundleSavingsEm.textContent = `Poupas ${this.formatMoney(bundleTotals.savings)}`;
    }

    const singleVariant = this.singleVariant;
    if (singleVariant?.price) {
      document.querySelectorAll('.product-details .price-item--regular, .product-information .price-item--regular, .product-details [data-product-price]').forEach((el) => {
        el.textContent = this.formatMoney(singleVariant.price);
      });
    }

    this.updateSubmitButton();
  }

  handleClick = (event) => {
    const modeButton = event.target.closest('[data-elevo-mode]');
    if (modeButton) {
      const newMode = modeButton.dataset.elevoMode;
      if (this.state.mode !== newMode) {
        this.state.mode = newMode;
        if (this.state.mode === 'bundle') {
          this.state.bundle[0] = { ...this.state.single };
        }
        this.updateModeDOM();
        this.verifySelectedVariantStock();
      }
      return;
    }

    const heightToggle = event.target.closest('[data-elevo-bundle-height-toggle]');
    if (heightToggle) {
      const index = Number(heightToggle.dataset.elevoBundleHeightToggle);
      this.state.bundleHeightOpen[index] = !this.state.bundleHeightOpen[index];
      const modelContainer = heightToggle.closest('.elevo-builder__bundle-model');
      if (modelContainer) {
        const picker = modelContainer.querySelector('.elevo-builder__bundle-model-picker');
        const chevron = modelContainer.querySelector('.elevo-builder__chevron');
        if (picker) picker.classList.toggle('is-open', this.state.bundleHeightOpen[index]);
        if (chevron) chevron.classList.toggle('is-open', this.state.bundleHeightOpen[index]);
      }
      return;
    }

    const optionButton = event.target.closest('[data-elevo-option]');
    if (optionButton) {
      const status = this.querySelector('[data-elevo-status]');
      if (status) status.textContent = '';
      const type = optionButton.dataset.elevoOption;
      const value = optionButton.dataset.elevoValue;
      if (optionButton.dataset.elevoScope === 'bundle') {
        const index = Number(optionButton.dataset.elevoIndex || 0);
        this.state.bundle[index] = { ...this.state.bundle[index], [type]: value };
        if (type === 'height') this.state.bundleHeightOpen[index] = false;
      } else {
        this.state.single = { ...this.state.single, [type]: value };
        this.state.bundle[0] = { ...this.state.single };
      }
      this.updateOptionsDOM(optionButton);
      if (optionButton.dataset.elevoScope === 'single') this.syncSelectedVariantMedia();
      this.verifySelectedVariantStock();
      return;
    }

    const quantityButton = event.target.closest('[data-elevo-quantity]');
    if (quantityButton) {
      this.state.quantity = Math.max(1, this.state.quantity + Number(quantityButton.dataset.elevoQuantity));
      this.render();
    }
  };

  syncSelectedVariantMedia() {
    const variant = this.singleVariant;
    if (!variant?.id || !this.product.url) return;

    this.updatePrimaryMediaImage(variant);

    const url = new URL(this.product.url, window.location.origin);
    url.searchParams.set('variant', variant.id);
    history.replaceState({}, '', url.toString());
  }

  syncGalleryModelOrder() {
    const mediaGallery = document.querySelector('.product-information__media media-gallery');
    if (!mediaGallery) return;

    const reorder = () => {
      const slides = Array.from(mediaGallery.querySelectorAll('slideshow-slides > slideshow-slide'));
      const thumbnailButtons = Array.from(
        mediaGallery.querySelectorAll('.slideshow-controls__thumbnails > .slideshow-controls__thumbnail')
      );
      if (slides.length === 0 || thumbnailButtons.length !== slides.length) return false;

      const mediaIdsByRank = new Map();
      this.variantIndex.forEach((variant) => {
        if (!variant.mediaId) return;
        const rank = variant.height === '+2.5cm' ? 0 : variant.height === '+3.5cm' ? 1 : 2;
        const currentRank = mediaIdsByRank.get(variant.mediaId);
        mediaIdsByRank.set(variant.mediaId, currentRank === undefined ? rank : Math.min(currentRank, rank));
      });

      const getMediaId = (slide) => slide.querySelector('.product-media[data-media-id]')?.dataset.mediaId || '';
      const orderedIndexes = slides
        .map((slide, index) => ({ index, rank: mediaIdsByRank.get(getMediaId(slide)) ?? 3 }))
        .sort((a, b) => a.rank - b.rank || a.index - b.index)
        .map(({ index }) => index);

      const slideContainer = slides[0].parentElement;
      const thumbnailContainer = thumbnailButtons[0].parentElement;
      orderedIndexes.forEach((index) => {
        slideContainer.append(slides[index]);
        thumbnailContainer.append(thumbnailButtons[index]);
      });

      this.updatePrimaryMediaImage(this.singleVariant);
      return true;
    };

    if (!reorder()) {
      requestAnimationFrame(() => {
        if (!reorder()) requestAnimationFrame(reorder);
      });
    }
  }

  updatePrimaryMediaImage(variant) {
    const imageUrl = variant?.image || this.product.featured_image;
    if (!imageUrl) return;

    const mediaGallery = document.querySelector('.product-information__media media-gallery');
    if (!mediaGallery) return;

    const normalizeUrl = (urlStr) => {
      if (!urlStr) return '';
      return String(urlStr)
        .replace(/^https?:/i, '')
        .replace(/^\/\//, '')
        .split('?')[0]
        .split('#')[0];
    };

    const variantUrl = normalizeUrl(variant.image);
    const targetUrl = normalizeUrl(imageUrl);

    if (mediaGallery.slideshow && typeof mediaGallery.slideshow.select === 'function') {
      if (variant.mediaId) {
        const mediaSlide = mediaGallery.querySelector(`.product-media[data-media-id="${variant.mediaId}"]`)?.closest('slideshow-slide');
        const slides = Array.from(mediaGallery.querySelectorAll('slideshow-slide'));
        const slideIndex = mediaSlide ? slides.indexOf(mediaSlide) : -1;

        if (slideIndex !== -1) {
          mediaGallery.slideshow.select(slideIndex, undefined, { animate: false });
          return;
        }
      }

      const modelGalleryIndex = variant?.height === '+3.5cm' ? 1 : variant?.height === '+2.5cm' ? 0 : -1;
      if (modelGalleryIndex !== -1) {
        const slides = Array.from(mediaGallery.querySelectorAll('slideshow-slide'));
        const productSlides = slides.filter((slide) => slide.querySelector('.product-media img, .product-media__image'));
        const modelSlide = productSlides[modelGalleryIndex];
        const slideIndex = modelSlide ? slides.indexOf(modelSlide) : -1;

        if (slideIndex !== -1) {
          mediaGallery.slideshow.select(slideIndex, undefined, { animate: false });
          return;
        }
      }

      const mediaElements = Array.from(mediaGallery.querySelectorAll('.product-media img, .product-media__image'));
      const matchingIndex = mediaElements.findIndex((img) => {
        const imgSrc = normalizeUrl(img.src);
        return imgSrc && (
          (variantUrl && (imgSrc.includes(variantUrl) || variantUrl.includes(imgSrc))) ||
          (targetUrl && (imgSrc.includes(targetUrl) || targetUrl.includes(imgSrc)))
        );
      });

      if (matchingIndex !== -1) {
        mediaGallery.slideshow.select(matchingIndex, undefined, { animate: false });
        return;
      }
    }

    // Only update the first (primary) image if no matching slide is found — DO NOT overwrite all slideshow images!
    const mediaElements = Array.from(mediaGallery.querySelectorAll('.product-media img, .product-media__image'));
    const firstImg = mediaElements[0];
    if (firstImg && normalizeUrl(firstImg.src) !== targetUrl) {
      firstImg.src = imageUrl;
      if (firstImg.hasAttribute('srcset')) {
        firstImg.srcset = imageUrl;
      }
      firstImg.alt = `${this.product.title} ${variant.title || ''}`.trim();
    }
  }

  async refreshCart() {
    const response = await fetch(`${Theme.routes.cart_url}.json`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
    });
    if (!response.ok) throw new Error(`Erro ao atualizar carrinho: ${response.status}`);
    return response.json();
  }

  handleSubmit = async (event) => {
    event.preventDefault();
    const status = this.querySelector('[data-elevo-status]');
    if (this.state.mode === 'bundle' && !this.isBundleComplete()) {
      this.updateSubmitButton({ label: 'Escolhe as opções para os 3 pares', disabled: true });
      return;
    }

    const variants = this.state.mode === 'bundle'
      ? this.state.bundle.map((s) => this.findVariant(s)).filter(Boolean)
      : [this.state.mode === 'product-bundle' ? this.productBundleVariant : this.singleVariant].filter(Boolean);
    const totals = this.getTotals();
    const freeIndex = totals.freeIndex;
    const items = variants.map((variant, index) => {
      const isFreeLine = this.state.mode === 'bundle' && index === freeIndex;

      return {
        id: variant.id,
        quantity: this.state.quantity,
        properties: isFreeLine
          ? {
              '_ELEVO bundle': 'Artigo gratuito',
            }
          : undefined,
      };
    });

    this.updateSubmitButton({ loading: true });
    status.textContent = '';
    const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
    const deferredEventPromise = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({
          merchandiseId: item.id.toString(),
          quantity: item.quantity,
        })),
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message || 'Erro ao adicionar.');

      const cart = await this.refreshCart();
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'elevo-product-builder',
          itemCount: totalQuantity,
          productId: this.product.id,
          didError: false,
        },
      });

      this.updateSubmitButton({ label: 'Adicionado ao carrinho' });
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
      }
      window.setTimeout(() => this.updateSubmitButton(), 2500);
    } catch (error) {
      deferredEventPromise.reject(error);
      const selectedVariant = this.state.mode === 'product-bundle'
        ? this.productBundleVariant
        : this.singleVariant;
      const selectedVariantAvailable = this.state.mode === 'bundle'
        ? this.isBundleComplete()
        : this.isVariantAvailable(selectedVariant);
      const isOutOfStock = /sold out|esgotad|out of stock|sem stock|inventory/i.test(error.message || '');
      const canStillAdd = selectedVariantAvailable && !isOutOfStock;
      if (!canStillAdd) {
        this.updateSubmitButton({ label: 'Sem Stock', disabled: true });
      } else {
        this.updateSubmitButton({ label: 'Erro ao adicionar' });
      }
      status.textContent = '';
    }
  };
}

if (!customElements.get('elevo-product-builder')) {
  customElements.define('elevo-product-builder', ElevoProductBuilder);
}
